import { useLive, post, formatPhone, STAGE_LABEL, ago } from "../lib/api";
import type { BoardLead, Stage } from "../lib/api";
import { Score, Carregando, Avatar } from "../lib/ui";
import { Headset, Hourglass } from "lucide-react";

/*
 * Uma cor por etapa, do frio ao quente.
 *
 * O ponto no cabecalho da coluna faz o funil se ler da esquerda para a direita
 * como uma progressao, e nao como oito listas iguais.
 */
const COR_ETAPA: Partial<Record<Stage, string>> = {
  novo: "#7d8b88",
  engajado: "#8aa4c8",
  descoberta: "#9b8cd6",
  qualificado: "#3fcbc1",
  match: "#4fb3e0",
  visita_agendada: "#e0ad55",
  visita_realizada: "#e89a5c",
  proposta: "#6fcf97",
};

interface Board {
  stages: Stage[];
  leads: BoardLead[];
  summary: {
    stage: Stage;
    leads: number;
    fit_medio: number | null;
    temp_medio: number | null;
    horas_na_etapa: number | null;
  }[];
}

const ABERTAS: Stage[] = [
  "novo",
  "engajado",
  "descoberta",
  "qualificado",
  "match",
  "visita_agendada",
  "visita_realizada",
  "proposta",
];

/**
 * O kanban.
 *
 * Arrastar move a etapa de verdade e grava um `funnel_event` com autor humano.
 * O funil precisa ser editavel a mao: a IA acerta a maior parte, e a parte que
 * ela erra o corretor tem que poder corrigir sem pedir pra ninguem -- senao
 * ele para de confiar no numero e volta pra planilha.
 */
export function Funil({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const { data, reload } = useLive<Board>("/board", ["leads", "conversations"]);

  async function mover(leadId: string, stage: Stage) {
    await post(`/leads/${leadId}/stage`, { stage, reason: "movido no painel" });
    reload();
  }

  if (!data) return <Carregando />;

  const porEtapa = new Map<Stage, BoardLead[]>();
  for (const s of ABERTAS) porEtapa.set(s, []);
  for (const l of data.leads) porEtapa.get(l.stage)?.push(l);

  const resumo = new Map(data.summary.map((s) => [s.stage, s]));

  return (
    <div className="overflow-x-auto pb-4 -mx-1 px-1">
      <div className="flex gap-3 min-w-max">
        {ABERTAS.map((stage) => {
          const leads = porEtapa.get(stage) ?? [];
          const s = resumo.get(stage);
          return (
            <div
              key={stage}
              className="w-64 shrink-0 flex flex-col rounded-xl bg-surface/50 border border-rule p-2"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("text/lead");
                if (id) void mover(id, stage);
              }}
            >
              <div className="flex items-center justify-between px-1.5 pt-1 pb-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: COR_ETAPA[stage] ?? "#7d8b88" }}
                  />
                  {STAGE_LABEL[stage]}
                </h3>
                <span className="min-w-6 h-5 px-1.5 rounded-full bg-surface-3 text-[11px] font-semibold tabular-nums text-ink-2 grid place-items-center">
                  {leads.length}
                </span>
              </div>

              {s && s.leads > 0 && (
                <div className="px-1.5 pb-2 text-[11px] text-ink-3 tabular-nums">
                  fit médio {s.fit_medio ?? 0} · {s.horas_na_etapa ?? 0}h na etapa
                </div>
              )}

              <div className="flex flex-col gap-2 min-h-24">
                {leads.map((l) => (
                  <article
                    key={l.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/lead", l.id)}
                    onClick={() => onOpenLead(l.id)}
                    className="bg-surface-2 border border-rule rounded-lg p-3 cursor-grab active:cursor-grabbing
                      hover:border-rule-2 hover:-translate-y-px hover:shadow-[var(--shadow-pop)] transition-all"
                  >
                    <div className="flex items-center gap-2.5">
                      <Avatar nome={l.name} tamanho="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink truncate">
                          {l.name ?? "Sem nome"}
                        </div>
                        <div className="text-xs tabular-nums text-ink-3">
                          {formatPhone(l.phone_e164)}
                        </div>
                      </div>
                      {l.control === "pending_human" && (
                        <span title="esperando corretor" className="text-warm">
                          <Hourglass size={14} />
                        </span>
                      )}
                      {l.control === "human" && (
                        <span title="corretor atendendo" className="text-accent">
                          <Headset size={14} />
                        </span>
                      )}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <Score value={l.fit_score} label="fit" />
                      <Score value={l.temp_score} label="temp" tone="warm" />
                    </div>
                    <div className="flex justify-between items-center mt-2.5 pt-2 border-t border-rule text-[11px] text-ink-3">
                      <span>{l.source.replace("_", " ")}</span>
                      <span title="última mensagem do lead">
                        {ago(l.last_inbound_at)}
                      </span>
                    </div>
                  </article>
                ))}
                {leads.length === 0 && (
                  <div className="text-xs text-ink-3 text-center py-6 border border-dashed border-rule-2 rounded-lg">
                    Nenhum lead aqui
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
